/**
 * HomeScreen — port of garageiq-web/app/(main)/page.js.
 *
 * Faithful structure (hero → how it works → features → diagnostics → parts
 * → connected vehicle → featured guides → pricing → CTA), trimmed for a
 * single-column phone layout. Web idioms removed:
 *  - hover/group-hover styles dropped (no hover on touch devices)
 *  - md:/lg: responsive breakpoints flattened to single column
 *  - text-clip + bg-gradient text replaced with solid sky color
 *  - gradient/blur background pseudo-elements omitted; bg-navy is enough
 *  - <a> / next/link → navigation.navigate
 */
import React from 'react';
import { ScrollView, View, Text, Pressable } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { guides } from '../lib/guideData';
import {
  IconCar, IconSteps, IconCart, IconWrench, IconBook, IconDiagram, IconVideo,
  IconDiagnose, IconShop, IconSafety, IconParts, IconCheck,
} from '../components/Icons';

// ─── Reusable bits ──────────────────────────────────────────────────────────

function SectionHeading({ tag, title, subtitle }) {
  return (
    <View className="items-center mb-10">
      <View className="px-4 py-1.5 rounded-full border border-sky/20 bg-sky/10">
        <Text className="text-[11px] font-bold uppercase tracking-widest text-sky">{tag}</Text>
      </View>
      <Text className="text-3xl font-black text-white mt-4 mb-2 text-center">{title}</Text>
      {subtitle && <Text className="text-base text-white/50 text-center">{subtitle}</Text>}
    </View>
  );
}

const DIFFICULTY_BADGE = {
  beginner:     { wrap: 'bg-green-500/10 border-green-500/20',   text: 'text-green-400' },
  intermediate: { wrap: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-400' },
  advanced:     { wrap: 'bg-red-500/10 border-red-500/20',       text: 'text-red-400' },
};

function DifficultyBadge({ level = 'beginner' }) {
  const s = DIFFICULTY_BADGE[level] || DIFFICULTY_BADGE.beginner;
  return (
    <View className={`px-2.5 py-1 rounded-full border ${s.wrap}`}>
      <Text className={`text-[11px] font-bold capitalize ${s.text}`}>{level}</Text>
    </View>
  );
}

function FeatureCard({ icon, title, desc, badge }) {
  return (
    <View className="bg-charcoal/50 border border-white/10 rounded-2xl p-6">
      <View className="mb-3">{icon}</View>
      <Text className="text-base font-black text-white mb-1.5">{title}</Text>
      <Text className="text-sm text-white/50 leading-5">{desc}</Text>
      {badge && (
        <View className="self-start mt-3 px-3 py-1 rounded-full bg-sky/10 border border-sky/20">
          <Text className="text-[11px] font-bold text-sky">{badge}</Text>
        </View>
      )}
    </View>
  );
}

function HowItWorksCard({ step, icon, title, desc }) {
  return (
    <View className="bg-charcoal/50 border border-white/10 rounded-2xl p-6 items-center">
      <View className="w-10 h-10 rounded-xl bg-sky items-center justify-center mb-3">
        <Text className="text-white font-black text-lg">{step}</Text>
      </View>
      <View className="mb-3">{icon}</View>
      <Text className="font-bold text-white mb-1.5 text-center">{title}</Text>
      <Text className="text-sm text-white/50 text-center leading-5">{desc}</Text>
    </View>
  );
}

function Divider() {
  return <View className="h-px bg-white/10 mx-6" />;
}

// ─── Main screen ────────────────────────────────────────────────────────────

export default function HomeScreen() {
  const navigation = useNavigation();
  const featuredGuides = guides.slice(0, 3);

  return (
    <ScrollView
      className="flex-1 bg-navy"
      contentContainerStyle={{ paddingBottom: 48 }}
      showsVerticalScrollIndicator={false}
    >
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <View className="px-6 pt-10 pb-12 items-center">
        <View className="flex-row items-center gap-2 px-4 py-1.5 rounded-full bg-sky/10 border border-sky/20 mb-6">
          <View className="w-2 h-2 rounded-full bg-sky" />
          <Text className="text-[11px] font-bold text-sky">NOW IN EARLY ACCESS</Text>
        </View>

        <Text className="text-5xl font-black text-white text-center leading-tight">Your Car.</Text>
        <Text className="text-5xl font-black text-white text-center leading-tight">Your Garage.</Text>
        <Text className="text-5xl font-black text-sky text-center leading-tight mb-5">Your IQ.</Text>

        <Text className="text-base text-white/50 text-center leading-6 mb-8">
          GarageIQ turns professional automotive repair guides into AI-powered, step-by-step instructions
          anyone can follow — with AI diagnostics, creator videos, and one-tap parts ordering.
        </Text>

        <Pressable
          onPress={() => navigation.navigate('VehicleSelector')}
          className="bg-charcoal/60 border border-white/10 rounded-2xl px-6 py-5 w-full active:opacity-80"
        >
          <Text className="text-white/60 text-sm font-semibold mb-2 text-center">
            Add your vehicle to get started
          </Text>
          <View className="bg-sky rounded-xl py-3 items-center">
            <Text className="text-white font-bold">+ Choose Vehicle</Text>
          </View>
        </Pressable>

        <Pressable
          onPress={() => navigation.navigate('MainTabs', { screen: 'Garage' })}
          className="mt-4 active:opacity-60"
        >
          <Text className="text-sm font-semibold text-sky">Open My Garage →</Text>
        </Pressable>

        {/* Stats */}
        <View className="mt-8 pt-6 border-t border-white/10 w-full flex-row justify-around">
          {[
            { num: '$1,200', label: 'avg. annual savings' },
            { num: 'Any Car', label: 'year, make & model' },
            { num: '0 Exp.', label: 'required to start' },
          ].map(s => (
            <View key={s.label} className="items-center px-2 flex-1">
              <Text className="text-2xl font-black text-sky">{s.num}</Text>
              <Text className="text-[11px] text-white/50 mt-1 text-center">{s.label}</Text>
            </View>
          ))}
        </View>
      </View>

      <Divider />

      {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
      <View className="py-12 px-6">
        <SectionHeading
          tag="How It Works"
          title="From zero to job complete."
          subtitle="No mechanical background needed."
        />
        <View className="gap-3">
          <HowItWorksCard step="1" icon={<IconCar size={28} />} title="Enter Your Vehicle" desc="Year, make, and model. Every guide is tailored to your exact car." />
          <HowItWorksCard step="2" icon={<IconSteps size={28} />} title="Pick Your Task" desc="Browse maintenance guides or let the mileage tracker tell you what's due." />
          <HowItWorksCard step="3" icon={<IconCart size={28} />} title="Order Your Parts" desc="One tap fills your AutoZone or O'Reilly cart with the exact parts you need." />
          <HowItWorksCard step="4" icon={<IconWrench size={28} />} title="Follow the Guide" desc="Step-by-step instructions with diagrams and creator videos at each step." />
        </View>
      </View>

      <Divider />

      {/* ── AI-SIMPLIFIED GUIDES ─────────────────────────────────────────── */}
      <View className="py-12 px-6">
        <SectionHeading
          tag="Independently Created Guides"
          title="Expert content, built for beginners."
          subtitle="Independent research, multi-source verification, technician-tested."
        />
        <View className="gap-3">
          <FeatureCard icon={<IconBook size={28} />} title="Step-by-Step Instructions" desc="Complex procedures broken into simple, numbered steps. Every torque spec, part number, and safety warning included." />
          <FeatureCard icon={<IconDiagram size={28} />} title="Diagrams at Every Step" desc="Annotated photos and diagrams so you know exactly which bolt is which. No guessing." />
          <FeatureCard icon={<IconVideo size={28} />} title="Creator Videos" desc="Curated YouTube creators embedded directly into each step. Watch the procedure on your exact vehicle." />
        </View>

        <View className="mt-6 self-center flex-row items-center gap-3 bg-charcoal/40 border border-white/10 rounded-xl px-4 py-3">
          <IconSafety size={18} />
          <Text className="text-xs text-white/40 flex-1">Independently created — not OEM documentation.</Text>
        </View>
      </View>

      <Divider />

      {/* ── AI DIAGNOSTICS ───────────────────────────────────────────────── */}
      <View className="py-12 px-6">
        <SectionHeading
          tag="AI Diagnostics"
          title="Snap a photo. Get an answer."
          subtitle="No more guessing what's wrong with your car."
        />
        <View className="gap-3">
          <FeatureCard icon={<IconDiagnose size={28} />} title="Photo & Video Diagnosis" desc="Take a photo of a warning light, fluid leak, or damaged part. The AI identifies the issue and links to the repair guide." />
          <FeatureCard icon={<IconParts size={28} />} title="OBD-II Code Reading" desc="Pair a $10–$25 Bluetooth scanner. Read DTCs, translate to plain English, see the exact fix." />
          <FeatureCard icon={<IconSafety size={28} />} title="Independently Verified" desc="Not generic LLM answers. Each guide is researched, verified, and accuracy-checked." />
          <FeatureCard icon={<IconWrench size={28} />} title="Diagnosis to Fix in One Tap" desc="Every diagnosis links directly to the repair guide and auto-fills your parts cart." />
        </View>
      </View>

      <Divider />

      {/* ── PARTS ORDERING ───────────────────────────────────────────────── */}
      <View className="py-12 px-6">
        <SectionHeading
          tag="Integrated Parts Ordering"
          title="One tap. Exact parts. Your cart."
        />
        <View className="gap-3">
          <FeatureCard icon={<IconShop size={28} />} title="AutoZone API" desc="Full programmatic integration. Auto-populates your cart with exact parts, checks inventory, handles checkout." badge="Primary Partner" />
          <FeatureCard icon={<IconWrench size={28} />} title="O'Reilly Auto Parts" desc="Secondary integration bringing another major retailer to the platform. More choices, competitive pricing." badge="Phase 2" />
          <FeatureCard icon={<IconCart size={28} />} title="RockAuto" desc="Budget-friendly online option via affiliate integration. Perfect for price-conscious DIYers." badge="Affiliate" />
        </View>
      </View>

      <Divider />

      {/* ── FEATURED GUIDES ──────────────────────────────────────────────── */}
      <View className="py-12 px-6">
        <SectionHeading tag="Popular Guides" title="Start here." subtitle="Real guides, ready to follow." />
        <View className="gap-3">
          {featuredGuides.map(guide => (
            <Pressable
              key={guide.id}
              onPress={() => navigation.navigate('GuideDetail', { id: guide.id, title: guide.title })}
              className="bg-charcoal/40 border border-white/10 rounded-2xl p-5 active:opacity-80"
            >
              <View className="flex-row items-start justify-between mb-2">
                <DifficultyBadge level={guide.difficulty} />
                <Text className="text-xs text-white/30 font-medium">{guide.duration}</Text>
              </View>
              <Text className="text-base font-black text-white mb-1">{guide.title}</Text>
              <Text className="text-xs text-white/40 mb-3">
                {guide.vehicle.year} {guide.vehicle.make} {guide.vehicle.model} {guide.vehicle.trim}
              </Text>
              <Text className="text-sm text-white/50 leading-5" numberOfLines={3}>
                {guide.description}
              </Text>
              <View className="mt-4 pt-3 border-t border-white/10 flex-row items-center justify-between">
                <Text className="text-xs text-white/30">{guide.steps?.length ?? 0} steps</Text>
                <Text className="text-sm font-bold text-sky">Start Guide →</Text>
              </View>
            </Pressable>
          ))}
        </View>

        <Pressable
          onPress={() => navigation.navigate('MainTabs', { screen: 'Guides' })}
          className="mt-6 self-center bg-charcoal/40 border border-white/10 rounded-xl px-8 py-3 active:opacity-80"
        >
          <Text className="text-white font-bold text-sm">View All Guides →</Text>
        </Pressable>
      </View>

      <Divider />

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <View className="py-12 px-6">
        <SectionHeading
          tag="Pricing"
          title="Start free."
          subtitle="Every plan includes AI diagnostics, guides, and parts ordering."
        />
        <View className="gap-3">
          {[
            { name: 'Free', price: '$0', period: '/forever', desc: 'Perfect for a single vehicle',
              features: ['1–3 vehicles', 'All maintenance guides', 'AI photo diagnostics', 'Parts ordering', 'Ad-supported videos'],
              cta: 'Get Started', highlight: false },
            { name: 'Garage Pro', price: '$10', period: '/month', desc: 'For the multi-car household',
              features: ['Up to 10 vehicles', 'Ad-free experience', 'GPS mileage tracking', 'Priority AI diagnostics', 'Maintenance reminders'],
              cta: 'Start Free Trial', highlight: true },
            { name: 'Fleet', price: '$200', period: '/month', desc: 'For businesses and collectors',
              features: ['Unlimited vehicles', 'Fleet dashboard', 'Maintenance scheduling', 'Expense tracking', 'Dedicated support'],
              cta: 'Contact Us', highlight: false },
          ].map(plan => (
            <View key={plan.name} className={`rounded-2xl p-6 ${plan.highlight ? 'bg-sky/10 border-2 border-sky/40' : 'bg-charcoal/40 border border-white/10'}`}>
              {plan.highlight && (
                <View className="self-start mb-3 px-3 py-1 rounded-full bg-sky">
                  <Text className="text-[11px] font-bold text-navy">MOST POPULAR</Text>
                </View>
              )}
              <Text className="text-lg font-black text-white">{plan.name}</Text>
              <View className="flex-row items-baseline mb-2">
                <Text className="text-4xl font-black text-sky">{plan.price}</Text>
                <Text className="text-white/30 text-sm ml-1">{plan.period}</Text>
              </View>
              <Text className="text-white/40 text-sm mb-4">{plan.desc}</Text>
              <View className="gap-2 mb-5">
                {plan.features.map((f, i) => (
                  <View key={i} className="flex-row items-start gap-2">
                    <View className="mt-1"><IconCheck size={14} /></View>
                    <Text className="text-sm text-white/60 flex-1">{f}</Text>
                  </View>
                ))}
              </View>
              <Pressable className={`py-3 rounded-xl items-center active:opacity-80 ${plan.highlight ? 'bg-sky' : 'bg-white/10'}`}>
                <Text className="text-white font-bold text-sm">{plan.cta}</Text>
              </Pressable>
            </View>
          ))}
        </View>
      </View>

      <Divider />

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <View className="py-12 px-6 items-center">
        <Text className="text-3xl font-black text-white text-center mb-3">
          Ready to get your hands dirty?
        </Text>
        <Text className="text-white/50 mb-6 text-center">
          Pick your first guide and see how easy car maintenance really is.
        </Text>
        <View className="gap-3 w-full">
          <Pressable
            onPress={() => navigation.navigate('MainTabs', { screen: 'Guides' })}
            className="bg-sky rounded-2xl py-4 items-center active:opacity-80"
          >
            <Text className="text-white font-black text-lg">Browse All Guides →</Text>
          </Pressable>
          <Pressable
            onPress={() => navigation.navigate('Invest')}
            className="bg-charcoal/40 border border-white/10 rounded-2xl py-3.5 items-center active:opacity-80"
          >
            <Text className="text-white font-bold">Investor Info</Text>
          </Pressable>
          <Pressable
            onPress={() => navigation.navigate('ShopSignup')}
            className="bg-charcoal/40 border border-white/10 rounded-2xl py-3.5 items-center active:opacity-80"
          >
            <Text className="text-white font-bold">List Your Shop</Text>
          </Pressable>
        </View>
      </View>
    </ScrollView>
  );
}
